import { useCallback, useEffect, useState } from 'react';

export interface RenderDirective {
  key: string;
  props: Record<string, unknown>;
}

export interface Message {
  role: 'user' | 'bilbo';
  content: string;
  at: number;
  /** Opt-in chat-bubble component mount; not persisted across reloads. */
  render?: RenderDirective | null;
}

const STORAGE_KEY = 'bilbo:chat:current';
const MAX_TURNS = 80;

function stripEphemeral(messages: Message[]): Message[] {
  // Drop the `render` directive when persisting — components are one-shot
  // and would not be remountable across page reloads anyway. The textual
  // `content` (the tool's `display` field) is the durable record.
  return messages.map(({ render: _render, ...rest }) => rest);
}

export function useTranscript(): [
  Message[],
  (m: Message) => void,
  () => void,
  (index: number, patch: Partial<Message>) => void,
] {
  const [messages, setMessages] = useState<Message[]>(() => {
    if (typeof localStorage === 'undefined') return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Message[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stripEphemeral(messages)));
    } catch {
      // localStorage full or unavailable — silent
    }
  }, [messages]);

  const add = useCallback((m: Message) => {
    setMessages((prev) => {
      const next = [...prev, m];
      return next.length > MAX_TURNS ? next.slice(-MAX_TURNS) : next;
    });
  }, []);

  const update = useCallback((index: number, patch: Partial<Message>) => {
    setMessages((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next[index] = { ...next[index]!, ...patch };
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setMessages([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return [messages, add, clear, update];
}
